import { Alert, Button, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useDashboardCostGovernance } from '../../apps/apis/claudeCodeDashboard/hooks';
import type {
  DashboardCostGovernanceBreakdown,
  DashboardCostGovernanceDailyCost,
  DashboardCostGovernanceSessionCandidate,
} from '../../apps/apis/claudeCodeDashboard/types';
import { ROUTES } from '../../routes/routes';
import { HELP_TEXT } from '../../components/claude-code/agentTelemetryHelp';
import { HelpBadge, HelpLabel } from '../../components/claude-code/HelpTooltip';

const REVIEW_LIMIT = 25;

interface AgentCostGovernancePanelProps {
  windowDays: number;
}

const formatCost = (value: number, digits = 2): string => `$${value.toFixed(digits)}`;
const formatNumber = (value: number): string => value.toLocaleString();

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const formatRoles = (roles: string[]): string => (roles.length ? roles.join(', ') : 'unassigned');

const AgentCostGovernancePanel = ({ windowDays }: AgentCostGovernancePanelProps) => {
  const query = useDashboardCostGovernance(windowDays, REVIEW_LIMIT);

  return (
    <Card className="mb-3 border-warning-subtle">
      <Card.Body>
        <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap mb-3">
          <div>
            <Card.Title className="h5 mb-1">
              <HelpLabel description="Aggregate token/cost review for AX usage. This page is for coaching and cost hygiene, not employee scoring.">
                Cost governance
              </HelpLabel>
            </Card.Title>
            <div className="text-muted small">
              Aggregate-first AX cost review. Use this to spot unusual growth, not to score individual employees.
            </div>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            Refresh
          </Button>
        </div>

        {query.isLoading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        ) : query.error ? (
          <Alert variant="danger">Failed to load cost governance: {(query.error as Error).message}</Alert>
        ) : query.data ? (
          <>
            <Row className="g-2 mb-3">
              <SummaryCard
                title="Total cost"
                description={HELP_TEXT.costUsd}
                value={formatCost(query.data.summary.totalCostUsd)}
                detail={`${formatNumber(query.data.summary.sessions)} sessions`}
                variant="warning"
              />
              <SummaryCard
                title="Input tokens"
                description={HELP_TEXT.inputTokens}
                value={formatNumber(query.data.summary.inputTokens)}
                detail="window total"
              />
              <SummaryCard
                title="Output tokens"
                description={HELP_TEXT.outputTokens}
                value={formatNumber(query.data.summary.outputTokens)}
                detail="window total"
              />
              <SummaryCard
                title="Active employees"
                description={HELP_TEXT.activeEmployees}
                value={formatNumber(query.data.summary.activeEmployees)}
                detail="with cost metrics"
              />
              <SummaryCard
                title="Review candidates"
                description={HELP_TEXT.highCostLowMcp}
                value={formatNumber(query.data.summary.highCostLowMcpSessions)}
                detail="high cost, no MCP"
                variant={query.data.summary.highCostLowMcpSessions > 0 ? 'danger' : 'secondary'}
              />
            </Row>

            <Row className="g-3">
              <Col xl={5}>
                <DailyCostTable rows={query.data.dailyCost} />
              </Col>
              <Col xl={3}>
                <BreakdownTable title="Cost by role" rows={query.data.roleCost} />
              </Col>
              <Col xl={4}>
                <BreakdownTable title="Cost by model" rows={query.data.modelCost} />
              </Col>
            </Row>

            <div className="mt-3">
              <SessionCandidateTable rows={query.data.highCostLowMcpSessions} />
            </div>
          </>
        ) : null}
      </Card.Body>
    </Card>
  );
};

function SummaryCard({
  title,
  description,
  value,
  detail,
  variant = 'primary',
}: {
  title: string;
  description: string;
  value: string;
  detail: string;
  variant?: string;
}) {
  return (
    <Col sm={6} lg>
      <Card className="h-100 bg-light border-0">
        <Card.Body className="py-3">
          <div className="text-muted small">
            <HelpLabel description={description}>{title}</HelpLabel>
          </div>
          <div className={`h4 mb-1 text-${variant}`}>{value}</div>
          <div className="text-muted small">{detail}</div>
        </Card.Body>
      </Card>
    </Col>
  );
}

function DailyCostTable({ rows }: { rows: DashboardCostGovernanceDailyCost[] }) {
  if (rows.length === 0) {
    return <EmptyCard title="Daily cost trend" message="No cost metrics in this window." />;
  }
  return (
    <Card className="h-100">
      <Card.Header className="fw-semibold">
        <HelpLabel description={HELP_TEXT.dailyCostTrend}>Daily cost trend</HelpLabel>
      </Card.Header>
      <Card.Body className="p-0">
        <Table size="sm" responsive className="mb-0 align-middle">
          <thead>
            <tr>
              <th>
                <HelpLabel description="Calendar date for the aggregated cost bucket.">Date</HelpLabel>
              </th>
              <th className="text-end">
                <HelpLabel description={HELP_TEXT.costUsd}>Cost</HelpLabel>
              </th>
              <th className="text-end">
                <HelpLabel description="Input plus output tokens for the day.">Tokens</HelpLabel>
              </th>
              <th>
                <HelpLabel description="Spike means cost growth crossed the backend spike threshold.">Signal</HelpLabel>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(-7).map((row) => (
              <tr key={row.date}>
                <td className="text-muted small">{row.date}</td>
                <td className="text-end">{formatCost(row.totalCostUsd, 3)}</td>
                <td className="text-end">{formatNumber(row.inputTokens + row.outputTokens)}</td>
                <td>
                  {row.isSpike ? (
                    <HelpBadge bg="danger" description="Cost increased sharply versus the previous day.">
                      Spike
                    </HelpBadge>
                  ) : (
                    <HelpBadge bg="secondary" description="No cost spike detected for this day.">
                      Normal
                    </HelpBadge>
                  )}
                  {row.costGrowthPct !== null ? (
                    <div className="text-muted small">{row.costGrowthPct}% vs prior day</div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

function BreakdownTable({ title, rows }: { title: string; rows: DashboardCostGovernanceBreakdown[] }) {
  if (rows.length === 0) {
    return <EmptyCard title={title} message="No breakdown data." />;
  }
  return (
    <Card className="h-100">
      <Card.Header className="fw-semibold">
        <HelpLabel description={title === 'Cost by role' ? HELP_TEXT.costByRole : HELP_TEXT.costByModel}>
          {title}
        </HelpLabel>
      </Card.Header>
      <Card.Body className="p-0">
        <Table size="sm" responsive className="mb-0 align-middle">
          <thead>
            <tr>
              <th>
                <HelpLabel description="Role or model bucket name for this cost breakdown.">Name</HelpLabel>
              </th>
              <th className="text-end">
                <HelpLabel description={HELP_TEXT.costUsd}>Cost</HelpLabel>
              </th>
              <th className="text-end">
                <HelpLabel description={HELP_TEXT.sessions}>Sessions</HelpLabel>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 5).map((row) => (
              <tr key={row.name}>
                <td>{row.name}</td>
                <td className="text-end">{formatCost(row.totalCostUsd, 3)}</td>
                <td className="text-end">{formatNumber(row.sessions)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

function SessionCandidateTable({ rows }: { rows: DashboardCostGovernanceSessionCandidate[] }) {
  if (rows.length === 0) {
    return (
      <EmptyCard title="High-cost low-MCP review candidates" message="No sessions crossed the review threshold." />
    );
  }
  return (
    <Card>
      <Card.Header className="fw-semibold">
        <HelpLabel description={HELP_TEXT.highCostLowMcp}>High-cost low-MCP review candidates</HelpLabel>
      </Card.Header>
      <Card.Body className="p-0">
        <Table size="sm" responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th>
                <HelpLabel description="Employee associated with this review candidate session.">Employee</HelpLabel>
              </th>
              <th>
                <HelpLabel description="Session start timestamp.">Started</HelpLabel>
              </th>
              <th>
                <HelpLabel description={HELP_TEXT.roles}>Roles</HelpLabel>
              </th>
              <th>
                <HelpLabel description="Model reported by telemetry when available.">Model</HelpLabel>
              </th>
              <th className="text-end">
                <HelpLabel description={HELP_TEXT.costUsd}>Cost</HelpLabel>
              </th>
              <th className="text-end">
                <HelpLabel description={HELP_TEXT.events}>Events</HelpLabel>
              </th>
              <th>
                <HelpLabel description="Why this session was selected for review.">Reason</HelpLabel>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.sessionId}>
                <td>
                  <Link to={ROUTES.agentTelemetry.employeeDetail(row.employeeId)}>{row.employeeId}</Link>
                </td>
                <td className="text-muted small">{formatDateTime(row.startedAt)}</td>
                <td className="text-muted small">{formatRoles(row.roles)}</td>
                <td>{row.model}</td>
                <td className="text-end">{formatCost(row.totalCostUsd, 3)}</td>
                <td className="text-end">
                  {formatNumber(row.totalEvents)}
                  <div className="text-muted small">{formatNumber(row.mcpEvents)} MCP</div>
                </td>
                <td>
                  <HelpBadge bg="warning" text="dark" description="High-cost session with little or no MCP usage.">
                    {row.reason}
                  </HelpBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

function EmptyCard({ title, message }: { title: string; message: string }) {
  return (
    <Card className="h-100">
      <Card.Header className="fw-semibold">{title}</Card.Header>
      <Card.Body className="text-muted small">{message}</Card.Body>
    </Card>
  );
}

export default AgentCostGovernancePanel;
